import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, ImageOff, Search as SearchIcon, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

/**
 * /black-1of1/report[?sticker=CODE]
 *
 * Phase 2 — Public "Report a Sighting" form.
 *  - Requires collector auth. Redirects to /login?next=… when logged out.
 *  - Sticker preselected when opened from a detail page; searchable
 *    selector when opened from the tracker hub.
 *  - Image upload uses the existing media_uploads / Emergent Object
 *    Storage pipeline (private until admin approves).
 *  - Success screen never modifies public tracker status.
 */
const SOURCE_TYPES = [
  ["instagram", "Instagram"],
  ["facebook", "Facebook"],
  ["tiktok", "TikTok"],
  ["ebay", "eBay"],
  ["reddit", "Reddit"],
  ["youtube", "YouTube"],
  ["other", "Other"],
];

export default function ReportSightingForm() {
  const auth = useCollectorAuth?.() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preselectedCode = (searchParams.get("sticker") || "").toUpperCase() || null;

  // ─── auth gate ────────────────────────────────────────────
  useEffect(() => {
    if (auth.loading) return;
    if (!auth.collector) {
      const next = `${location.pathname}${location.search || ""}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [auth.loading, auth.collector, location, navigate]);

  // ─── form state ───────────────────────────────────────────
  const [stickerCode, setStickerCode] = useState(preselectedCode || "");
  const [stickerLookup, setStickerLookup] = useState(null);
  const [stickerErr, setStickerErr] = useState(null);
  const [sourceType, setSourceType] = useState("instagram");
  const [sourceUrl, setSourceUrl] = useState("");
  const [publicAccount, setPublicAccount] = useState("");
  const [dateSeen, setDateSeen] = useState("");
  const [notes, setNotes] = useState("");
  const [ownsSticker, setOwnsSticker] = useState(false);
  const [displayPermission, setDisplayPermission] = useState(false);
  const [photoCredit, setPhotoCredit] = useState("");

  // Sticker selector (only when no preselection)
  const [stickerQuery, setStickerQuery] = useState("");
  const [stickerResults, setStickerResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Image
  const fileRef = useRef();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageMediaId, setImageMediaId] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // ─── sticker lookup on stickerCode change ─────────────────
  useEffect(() => {
    if (!stickerCode) { setStickerLookup(null); setStickerErr(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/black-1of1/${encodeURIComponent(stickerCode)}`);
        if (!cancelled) { setStickerLookup(r.data); setStickerErr(null); }
      } catch (e) {
        if (!cancelled) { setStickerLookup(null); setStickerErr(e?.response?.data?.detail || "Sticker not found"); }
      }
    })();
    return () => { cancelled = true; };
  }, [stickerCode]);

  // ─── searchable selector ─────────────────────────────────
  useEffect(() => {
    if (preselectedCode) return;
    let cancelled = false;
    if (!stickerQuery || stickerQuery.length < 2) { setStickerResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/black-1of1/list", { params: { q: stickerQuery } });
        if (!cancelled) setStickerResults((r.data.items || []).slice(0, 12));
      } catch { if (!cancelled) setStickerResults([]); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [stickerQuery, preselectedCode]);

  // ─── image handlers ──────────────────────────────────────
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} }
    setPreviewUrl(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/black-1of1/reports/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImageMediaId(r.data.media_id);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed.");
      setPreviewUrl(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearImage = () => {
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} }
    setPreviewUrl(null); setImageMediaId(null);
  };

  // ─── submit ──────────────────────────────────────────────
  const canSubmit = useMemo(() => (
    !!stickerLookup && !!sourceUrl.trim() &&
    (sourceUrl.startsWith("http://") || sourceUrl.startsWith("https://")) &&
    !uploading && !submitting &&
    // If an image was uploaded, publishing permission is required.
    (!imageMediaId || displayPermission)
  ), [stickerLookup, sourceUrl, uploading, submitting, imageMediaId, displayPermission]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/black-1of1/reports", {
        sticker_number: stickerCode.toUpperCase(),
        source_type: sourceType,
        source_url: sourceUrl.trim(),
        public_account: publicAccount.trim() || null,
        date_seen: dateSeen || null,
        notes: notes.trim() || null,
        owns_sticker: ownsSticker,
        image_media_id: imageMediaId || null,
        display_permission_granted: !!(imageMediaId && displayPermission),
        photo_credit: (imageMediaId && photoCredit.trim()) ? photoCredit.trim() : null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── done screen ─────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-[#0b0b0d] text-white" data-testid="report-sighting-success">
        <SEO title="Sighting submitted · Black 1/1 Tracker" />
        <div className="container-gm py-16 md:py-24 max-w-xl">
          <div className="rounded-2xl border border-emerald-400/40 bg-emerald-400/[0.05] p-8 md:p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-400/15 border border-emerald-400/50 flex items-center justify-center mb-5">
              <CheckCircle2 size={28} className="text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl mb-3">Thanks — your sighting has been submitted.</h1>
            <p className="text-white/60 text-sm md:text-base leading-relaxed mb-6">
              CollectorUp verifies every sighting before changing the public Tracker.
              We&rsquo;ll review your submission and, when confirmed, mark the sticker
              publicly surfaced. Your report never appears on the public Tracker
              until an admin approves it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/black-1of1" className="inline-flex items-center justify-center gap-1 px-5 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f0c458] text-black text-sm font-semibold" data-testid="report-back-tracker">
                Back to Tracker
              </Link>
              <button
                onClick={() => {
                  setDone(false); setSourceUrl(""); setPublicAccount(""); setDateSeen("");
                  setNotes(""); setOwnsSticker(false); clearImage();
                  if (!preselectedCode) { setStickerCode(""); setStickerQuery(""); }
                }}
                className="px-5 py-2.5 rounded-full border border-white/20 text-white/80 hover:bg-white/5 text-sm"
                data-testid="report-submit-another"
              >
                Report another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── form ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white" data-testid="report-sighting-form">
      <SEO title="Report a Black 1/1 · CollectorUp" />
      <div className="container-gm py-8 md:py-12 max-w-3xl">
        <Link to={preselectedCode ? `/black-1of1/${preselectedCode}` : "/black-1of1"}
              className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-4"
              data-testid="report-back-link">
          <ArrowLeft size={14}/> Back
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#d4af37] mb-2">
          Panini FIFA World Cup 2026 · Black 1/1 Tracker
        </div>
        <h1 className="font-display text-3xl md:text-5xl leading-[1.02] mb-2">Report a Sighting</h1>
        <p className="text-white/60 text-sm md:text-base max-w-2xl mb-8 md:mb-10">
          Seen a Black 1/1 in the wild? Share the source so we can verify and add it to the tracker.
          Every submission is reviewed before anything on the public tracker changes.
        </p>

        <form onSubmit={submit} className="space-y-6" data-testid="report-form">
          {/* Sticker */}
          <Field label="Sticker">
            {preselectedCode ? (
              <div className="rounded-lg border border-white/15 bg-white/[0.03] px-4 py-3 flex items-center justify-between" data-testid="report-sticker-preselected">
                <div>
                  <div className="font-mono text-xs text-[#d4af37]">{stickerCode}</div>
                  <div className="font-display text-lg leading-tight">{stickerLookup?.name || "…"}</div>
                  <div className="text-[11px] text-white/40 uppercase tracking-widest">{stickerLookup?.country_code}</div>
                </div>
                <Link to="/black-1of1/report" className="text-[11px] text-white/50 hover:text-white underline">Change</Link>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="report-sticker-search"
                    value={stickerQuery || stickerCode}
                    onChange={(e) => { setStickerQuery(e.target.value); setShowResults(true); }}
                    onFocus={() => setShowResults(true)}
                    placeholder="Search player, sticker code, or country…"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm text-white placeholder-white/40"
                  />
                </div>
                {showResults && stickerResults.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-white/15 bg-[#141419] shadow-2xl" data-testid="report-sticker-results">
                    {stickerResults.map((s) => (
                      <button
                        key={s.official_sticker_id}
                        type="button"
                        onClick={() => {
                          setStickerCode(s.sticker_number);
                          setStickerQuery(`${s.sticker_number} · ${s.name}`);
                          setShowResults(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex items-baseline justify-between gap-3"
                        data-testid={`report-sticker-option-${s.sticker_number}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm truncate">{s.name}</div>
                          <div className="text-[10px] font-mono text-[#d4af37] uppercase tracking-widest">{s.country_code}</div>
                        </div>
                        <div className="text-xs font-mono text-white/60">{s.sticker_number}</div>
                      </button>
                    ))}
                  </div>
                )}
                {stickerCode && stickerLookup && (
                  <div className="mt-2 text-xs text-emerald-300/90 font-mono" data-testid="report-sticker-confirmed">
                    ✓ {stickerCode} · {stickerLookup.name} ({stickerLookup.country_code})
                    {stickerLookup.status === "found" && (
                      <span className="ml-2 text-[10px] text-white/50">already found — thanks, extra evidence still helps.</span>
                    )}
                  </div>
                )}
                {stickerCode && stickerErr && (
                  <div className="mt-2 text-xs text-red-400">{stickerErr}</div>
                )}
              </div>
            )}
          </Field>

          {/* Source type */}
          <Field label="Source">
            <div className="flex flex-wrap gap-2" data-testid="report-source-types">
              {SOURCE_TYPES.map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSourceType(k)}
                  data-testid={`report-source-${k}`}
                  className={`text-[11px] px-3 py-1.5 rounded-full border font-mono uppercase tracking-widest transition ${
                    sourceType === k
                      ? "bg-[#d4af37] text-black border-[#d4af37]"
                      : "border-white/20 text-white/70 hover:bg-white/5"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>

          {/* Source URL */}
          <Field label="Source URL" required>
            <input
              type="url"
              data-testid="report-source-url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm"
              required
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Public account / seller (optional)">
              <input
                data-testid="report-public-account"
                value={publicAccount}
                onChange={(e) => setPublicAccount(e.target.value)}
                placeholder="@handle or seller name"
                className="w-full px-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm"
              />
            </Field>
            <Field label="Date seen (optional)">
              <input
                type="date"
                data-testid="report-date-seen"
                value={dateSeen}
                onChange={(e) => setDateSeen(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm"
              />
            </Field>
          </div>

          {/* Image */}
          <Field label="Photo / screenshot (optional)">
            {previewUrl ? (
              <div className="rounded-lg border border-white/10 overflow-hidden aspect-[4/3] max-w-md bg-black relative" data-testid="report-image-preview">
                <img src={previewUrl} alt="Preview" className="absolute inset-0 w-full h-full object-contain" />
                {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs">Uploading…</div>}
                <button type="button" onClick={clearImage} data-testid="report-image-clear"
                        className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/70 border border-white/20 hover:bg-black">Remove</button>
              </div>
            ) : (
              <label className="block cursor-pointer" data-testid="report-image-picker">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
                <div className="rounded-lg border-2 border-dashed border-white/15 hover:border-[#d4af37]/50 bg-white/[0.02] p-6 text-center transition">
                  <Upload size={20} className="mx-auto text-white/40 mb-2" />
                  <div className="text-sm text-white/70">Click to upload a JPEG, PNG, or WebP</div>
                  <div className="text-[11px] text-white/40 mt-1">Stored privately until an admin approves your sighting</div>
                </div>
              </label>
            )}
          </Field>

          {/* Publishing permission — required when an image is attached */}
          {imageMediaId && (
            <label className="flex items-start gap-3 rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/[0.05] p-4 cursor-pointer hover:border-[#d4af37]" data-testid="report-permission-wrap">
              <input
                type="checkbox"
                data-testid="report-permission"
                checked={displayPermission}
                onChange={(e) => setDisplayPermission(e.target.checked)}
                className="mt-1"
                required
              />
              <div>
                <div className="text-sm font-semibold">
                  Publishing permission <span className="text-[#d4af37]">(required if you upload a photo)</span>
                </div>
                <div className="text-[12px] text-white/70 mt-1 leading-relaxed">
                  I confirm that I own this photo or have permission to submit it, and I give CollectorUp permission to display this image on the Black Border 1 of 1 Tracker.
                </div>
              </div>
            </label>
          )}

          {/* Photo credit — optional, only shown alongside an uploaded image */}
          {imageMediaId && (
            <Field label="Photo credit (optional)">
              <input
                data-testid="report-photo-credit"
                value={photoCredit}
                onChange={(e) => setPhotoCredit(e.target.value)}
                maxLength={120}
                placeholder="@collectorname"
                className="w-full px-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm"
              />
              <div className="text-[11px] text-white/40 mt-1">How would you like us to credit the photo? Examples: @collectorname · Sticker owner · John Smith</div>
            </Field>
          )}

          {/* Notes */}
          <Field label="Notes (optional)">
            <textarea
              data-testid="report-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Anything else that will help us verify this sighting…"
              className="w-full px-3 py-2.5 rounded-lg border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm"
            />
          </Field>

          {/* Ownership */}
          <label className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-4 cursor-pointer hover:border-white/20" data-testid="report-owns-wrap">
            <input
              type="checkbox"
              data-testid="report-owns"
              checked={ownsSticker}
              onChange={(e) => setOwnsSticker(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-semibold">I own this sticker</div>
              <div className="text-[11px] text-white/50 mt-0.5">Optional. Ownership verification comes later — for now this just tags your sighting.</div>
            </div>
          </label>

          {/* Submit */}
          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="report-submit"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] hover:bg-[#f0c458] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold text-sm"
            >
              {submitting ? "Submitting…" : "Submit sighting for review"}
            </button>
            <Link to={preselectedCode ? `/black-1of1/${preselectedCode}` : "/black-1of1"}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-sm">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/50 mb-1.5">
        {label}{required && <span className="text-[#d4af37]"> *</span>}
      </div>
      {children}
    </div>
  );
}
